/*
    New template guidelines:
        - A new enum must be created via LightboxTemplateType.java with a unique ID that matches the `id` field below
        - be sure to explicitly set any departures from the default
            - e.g. don't want to display banner_text? set it to an empty string (as opposed to null due to nullish coalescing use)
            - otherwise, it will pull from the default template, which is received from the back end via the `/create-stub` endpoint
        - use px instead of rem - the backend `css/inline/modify` endpoint handles it better
        - for similar reasons, use rgba instead of hex colors
        - when providing styles for text (banner_text, lines, label_style, etc.), it's best to provide a color and font-size to preserve the editing experience after the fact
        - keep container max-width declarations under the mobile_breakpoint (<=767px) to ensure proper responsiveness
        - when providing multi-axis positioning e.g. `background-position` or `object-position`, use a string with two values separated by a space, e.g. `center center` or `left top` instead of a shorthand
*/
export default {
  data() {
    // need to calculate asset URLs here since computed properties wouldn't be available yet
    const _asset_url_base = (() => {
      if (this.$rc && this.$rc.getEnvironment() === 'dev') {
        return 'http://localhost:8081';
      }
      if (this.$rc && this.$rc.getEnvironment() === 'staging') {
        return 'https://staging-redchirp.com/app';
      }
      return 'https://app.redchirp.com/app';
    })();

    const _image_assets = {
      wine_pour: `${_asset_url_base}/lightbox-images/wine-pour.jpg`,
      wine_barrels: `${_asset_url_base}/lightbox-images/wine-barrels.jpg`,
      wine_essentials: `${_asset_url_base}/lightbox-images/wine-essentials.jpg`,
      wine_cheers: `${_asset_url_base}/lightbox-images/wine-cheers.jpg`,
    };

    return {
      mixin_base_templates: [
        {
          id: 'your-first-pour-for-less',
          template_overrides: {
            use_logo: true,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 720px; background: rgba(255,255,255,1); box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08); border-radius: 8px; display: grid; grid-template-columns: 250px minmax(350px, 1fr);',
              mobile_styles: 'grid-template-columns: 1fr; grid-template-rows: auto 1fr; border-radius: 4px;',
            },
            container_content: {
              style: '',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 36px; font-weight: bold; padding: 24px 0 8px 0; color: {primary_color};',
            },
            logo_image: {
              style:
                'max-width: 100%; margin: 0px 0px 0px 0px; padding: 0px 0px 0px 0px; object-fit: cover; object-position: center top;',
              mobile_styles: 'object-position: center top;',
              url: _image_assets.wine_pour,
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px;',
              mobile_styles: 'aspect-ratio: 2 / 1;',
            },
            close_icon: {
              style: 'color: rgba(0,0,0,0.9);',
              mobile_styles: 'color: rgba(255,255,255,1);', // will be overlaying image
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 32px; font-weight: bold; margin: 28px 0 12px 0; line-height: 1; color: {primary_color};',
              text: 'Your First Pour, For Less',
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 52px; font-weight: bold; color: {primary_color}; line-height: 1.25;',
              text: '',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; font-weight: 300; color: rgba(0,0,0,0.9); max-width: 85%; margin: auto;',
              text: 'Sign up for email and text updates to get 20% off your first order!',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'hidden',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'hidden',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin-top: 25px; color: rgba(0, 0, 0, 0.6);',
            },
            send_button: {
              style:
                'padding: 14px 0; background-color: {primary_color}; border: 2px solid {primary_color}; border-radius: 8px; width: 100%;',
            },
            send_button_text: {
              style:
                'color: rgba(255,255,255,1); font-family: {font_family}; font-size: 16px; font-weight: bold; text-transform: uppercase;',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 20px; font-weight: bold; padding: 45px 0; color: {primary_color};',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(0, 0, 0, 0.6);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0; background-color: {primary_color}; border-radius: 8px; width: 100%;',
            },
            thank_you_button_text: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); font-weight: bold; text-transform: uppercase;',
            },
          },
        },

        {
          id: 'become-an-insider',
          template_overrides: {
            use_logo: true,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 560px; background: rgba(255,255,255,1); box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08); border-radius: 8px;',
            },
            container_content: {
              style: `background-image: url(${_image_assets.wine_barrels}); background-size: cover; background-position: center center; background-repeat: no-repeat;`,
              mobile_styles: `background-image: url(${_image_assets.wine_barrels}); background-size: cover; background-position: center center; background-repeat: no-repeat;`,
            },
            container_background: {
              style: 'background: rgba(0,0,0,.35);',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 36px; font-weight: bold; color: {primary_color}; padding: 0px 0px 0px 0px;',
              text: '',
            },
            logo_image: {
              style:
                'max-width: 100%; margin: 0px 0px 0px 0px; padding: 0px 0px 0px 0px; object-fit: cover; object-position: center top;',
              url: '',
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px;',
            },
            close_icon: {
              style: 'color: rgba(255,255,255,.9);',
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 34px; font-weight: bold; margin: 36px 0 10px 0; line-height: 1; color: rgba(255,255,255,1);',
              text: 'Become an Insider',
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 52px; font-weight: bold; color: {primary_color}; line-height: 1.25;',
              text: '',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); max-width: 450px; margin: auto;',
              text:
                'Subscribe and be the first to know about new products, exclusive offers, and more!',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.8); margin-top: 4px;',
              field_mode: 'hidden',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.8); margin-top: 4px;',
              field_mode: 'hidden',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.8); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0; margin-bottom: 6px;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.8); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.8); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin: 25px 0 8px 0; color: rgba(255,255,255,1);',
            },
            send_button: {
              style:
                'padding: 14px 0; background-color: {primary_color}; border: 2px solid {primary_color}; border-radius: 8px; width: 100%; margin: 32px 0 32px 0;',
            },
            send_button_text: {
              style:
                'color: rgba(255,255,255,1); font-family: {font_family}; font-size: 16px; font-weight: bold; text-transform: uppercase;',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 20px; font-weight: bold; padding: 45px 0; color: rgba(255,255,255,1);',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(255,255,255,1);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0; background-color: {primary_color}; border-radius: 8px; width: 100%;',
            },
            thank_you_button_text: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); font-weight: bold; text-transform: uppercase;',
            },
          },
        },

        {
          id: 'enjoy-10',
          template_overrides: {
            use_logo: true,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 560px; background: rgba(255,255,255,1); box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08); border-radius: 8px;',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 36px; font-weight: bold; padding: 24px 0 8px 0; color: {primary_color};',
            },
            logo_image: {
              style:
                'max-width: 100%; margin: 0px 0px 0px 0px; object-fit: cover; object-position: center bottom;',
              url: _image_assets.wine_essentials,
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px; aspect-ratio: 3 / 1;',
            },
            close_icon: {
              style: 'color: rgba(255,255,255,1);',
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 18px; font-weight: 500; margin: 12px 0 0 0; color: rgba(0,0,0,0.8);',
              text: 'Enjoy',
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 52px; font-weight: bold; color: {primary_color}; line-height: 1.25;',
              text: '10% Off',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; font-weight: 300; color: rgba(0,0,0,0.8);',
              text: 'When you sign up for email and text messages!',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'hidden',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'hidden',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin-top: 25px; color: rgba(0, 0, 0, 0.5);',
            },
            send_button: {
              style:
                'padding: 14px 0; background-color: {primary_color}; border: 2px solid {primary_color}; border-radius: 8px; width: 100%;',
            },
            send_button_text: {
              style:
                'color: rgba(255,255,255,1); font-family: {font_family}; font-size: 16px; font-weight: bold; text-transform: uppercase;',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 20px; font-weight: bold; padding: 45px 0; color: {primary_color};',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(0, 0, 0, 0.8);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0; background-color: {primary_color}; border-radius: 8px; width: 100%;',
            },
            thank_you_button_text: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); font-weight: bold; text-transform: uppercase;',
            },
          },
        },

        {
          id: 'lets-be-friends',
          template_overrides: {
            use_logo: false,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 488px; background: {primary_color}; box-shadow: 0 0 20px rgba(0, 0, 0, 0.1); border-radius: 12px;',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 40px; font-weight: bold; padding: 16px 0 8px 0; text-align: center; color: rgba(255,255,255,0.9);',
              text: '',
            },
            logo_image: {
              style: 'max-width: 100%; margin: 0px 0px 0px 0px;',
              url: '',
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px;',
            },
            close_icon: {
              style: 'color: rgba(255,255,255,1);',
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 36px; line-height: 1.25; font-weight: bold; margin: 0 0 8px 0; color: rgba(255,255,255,0.9);',
              text: "Let's be Friends",
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 60px; line-height: 1.25; font-weight: bold; color: {primary_color};',
              text: '',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; line-height: 1.25; margin: 0px 0px 8px 0px; padding: 0 8px 0 8px; color: rgba(255,255,255,0.9);',
              text:
                'Get 10% off your first order! Join our mailing list to hear about new arrivals and promos',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: {primary_color}; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid rgba(220,220,220,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(255,255,255,.9); margin-top: 4px;',
              field_mode: 'hidden',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: {primary_color}; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid rgba(220,220,220,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(255,255,255,.9); margin-top: 4px;',
              field_mode: 'hidden',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: {primary_color}; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid rgba(220,220,220,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(255,255,255,.9); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: {primary_color}; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid rgba(220,220,220,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(255,255,255,.9); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: {primary_color}; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid rgba(220,220,220,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(255,255,255,.9); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin-top: 25px; color: rgba(255,255,255,.65);',
            },
            send_button: {
              style:
                'padding: 12px 0 12px 0; background-color: rgba(255,255,255,1); border-radius: 12px; width: 100%;',
            },
            send_button_text: {
              style:
                'color: {primary_color}; font-family: {font_family}; font-size: 16px; font-weight: 600;',
              text: 'Subscribe',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 32px; font-weight: bold; padding: 45px 0 45px 0; color: rgba(255,255,255,.9);',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(255,255,255,.9);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0 10px 0; background-color: rgba(255,255,255,1); border-radius: 12px; width: 100%;',
            },
            thank_you_button_text: {
              style: 'font-family: {font_family}; font-size: 14px; color: {primary_color};',
            },
          },
        },

        {
          id: 'sip-save-and-savor',
          template_overrides: {
            use_logo: false,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 460px; background: rgba(245,245,245,1); box-shadow: 0 0 20px rgba(0, 0, 0, 0.1); border-radius: 12px; border-top: 4px solid {primary_color};',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 40px; font-weight: bold; padding: 24px 0 0 0; text-align: center; color: {primary_color};',
              text: '',
            },
            logo_image: {
              style: 'max-width: 100%; margin: 0px 0px 0px 0px;',
              url: '',
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px; rgba(245,245,245,1);',
            },
            close_icon: {
              style: 'color: rgba(0,0,0,0.9);',
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 40px; line-height: 1.25; font-weight: bold; margin: 0 0 8px 0; color: {primary_color};',
              text: 'Sip, Save, and Savor',
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 60px; line-height: 1.25; font-weight: bold; color: {primary_color};',
              text: '',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; line-height: 1.25; margin: 0px 0px 24px 0px; color: rgba(0,0,0,0.75); max-width: 340px; margin: 0 auto 0 auto;',
              text:
                'Join our list for the latest updates and get discounted shipping on your first order!',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: rgba(245,245,245,1); padding: 8px 0 0 0; border-bottom: 1px solid rgba(140,140,140,1);',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 500; color: {primary_color}; margin-bottom: 6px; display: block; top: 12px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'hidden',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: rgba(245,245,245,1); padding: 8px 0 0 0; border-bottom: 1px solid rgba(140,140,140,1);',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 500; color: {primary_color}; margin-bottom: 6px; display: block; top: 12px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'hidden',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: rgba(245,245,245,1); padding: 8px 0 0 0; border-bottom: 1px solid rgba(140,140,140,1);',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 500; color: {primary_color}; margin-bottom: 6px; display: block; top: 12px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: rgba(245,245,245,1); padding: 8px 0 0 0; border-bottom: 1px solid rgba(140,140,140,1);',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 500; color: {primary_color}; margin-bottom: 6px; display: block; top: 12px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: rgba(245,245,245,1); padding: 8px 0 0 0; border-bottom: 1px solid rgba(140,140,140,1);',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 500; color: {primary_color}; margin-bottom: 6px; display: block; top: 12px;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.75); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin-top: 25px; color: rgba(0,0,0,0.75);',
            },
            send_button: {
              style:
                'padding: 12px 0 12px 0; background-color: {primary_color}; border-radius: 12px; width: 100%;',
            },
            send_button_text: {
              style:
                'color: rgba(255,255,255,1); font-family: {font_family}; font-size: 16px; font-weight: 600;',
              text: 'Sign Up',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 20px; font-weight: bold; padding: 45px 0 45px 0; color: {primary_color};',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(0,0,0,.8);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0 10px 0; background-color: {primary_color}; border-radius: 12px; width: 100%;',
            },
            thank_you_button_text: {
              style: 'font-family: {font_family}; font-size: 14px; color: rgba(255,255,255,1);',
            },
          },
        },

        {
          id: 'cheers-to-savings',
          template_overrides: {
            use_logo: true,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 592px; background: rgba(255,255,255,1); box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08); border-radius: 8px;',
            },
            container_content: {
              style: `background-image: url(${_image_assets.wine_cheers}); background-size: cover; background-position: center center; background-repeat: no-repeat;`,
              mobile_styles: `background-image: url(${_image_assets.wine_cheers}); background-size: cover; background-position: center center; background-repeat: no-repeat;`,
            },
            container_background: {
              style: 'background: rgba(0,0,0,.725);',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 36px; font-weight: bold; padding: 0px 0px 0px 0px; color: {primary_color};',
              text: '',
            },
            logo_image: {
              style:
                'max-width: 100%; margin: 0px 0px 0px 0px; padding: 0px 0px 0px 0px; object-fit: cover; object-position: center top;',
              url: '',
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px;',
            },
            close_icon: {
              style: 'color: rgba(255,255,255,.9);',
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 34px; font-weight: bold; margin: 36px 0 10px 0; line-height: 1; color: {primary_color}; filter: brightness(1.25);',
              text: 'Cheers to Savings!',
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 52px; font-weight: bold; color: {primary_color}; line-height: 1.25;',
              text: '',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); max-width: 450px; margin: 0 auto 24px auto;',
              text: 'Hop on the list for exclusive offers and updates on new arrivals',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.75); margin-top: 4px;',
              field_mode: 'optional',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.75); margin-top: 4px;',
              field_mode: 'optional',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.75); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0; margin-bottom: 6px;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.75); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: transparent; color: rgba(255,255,255,1); border: none; border-bottom: 2px solid {primary_color}; border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: rgba(255,255,255,1); margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; font-weight: bold; color: rgba(255,255,255,.75); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin: 25px 0 8px 0; color: rgba(255, 255, 255, 0.75);',
            },
            send_button: {
              style:
                'padding: 14px 0; background-color: {primary_color}; border: 2px solid {primary_color}; border-radius: 8px; width: 100%; margin: 32px 0 32px 0;',
            },
            send_button_text: {
              style:
                'color: rgba(255,255,255,1); font-family: {font_family}; font-size: 16px; font-weight: bold; text-transform: uppercase;',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 20px; font-weight: bold; padding: 45px 0; color: {primary_color};',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(255,255,255,1);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0; background-color: {primary_color}; border-radius: 8px; width: 100%;',
            },
            thank_you_button_text: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); font-weight: bold; text-transform: uppercase;',
            },
          },
        },

        {
          id: 'taste-more-pay-less',
          template_overrides: {
            use_logo: true,
            background_blur: {
              style: 'background-color: rgba(255,255,255,0.5); backdrop-filter: blur(10px);',
            },
            container: {
              style:
                'max-width: 460px; background: rgba(255,255,255,1); box-shadow: 0 4px 32px rgba(0, 0, 0, 0.08); border-radius: 8px;',
            },
            banner_text: {
              style:
                'font-family: {font_family}; font-size: 36px; font-weight: bold; padding: 24px 0 0 0; color: {primary_color}; font-style: italic;',
              text: '{Your Logo Here}',
            },
            logo_image: {
              style:
                'max-width: 60%; margin: 0px 0px 0px 0px; padding: 12px 12px 0 12px; object-fit: contain; object-position: center top;',
              url: '',
            },
            header_background: {
              style: 'padding: 0px 0px 0px 0px;',
            },
            close_icon: {
              style: 'color: rgba(0,0,0,.9);',
            },
            line_1: {
              style:
                'font-family: {font_family}; font-size: 18px; font-weight: 500; margin: 8px 0 0 0; color: {primary_color};',
              text: '',
            },
            line_2: {
              style:
                'font-family: {font_family}; font-size: 40px; font-weight: bold; color: {primary_color}; line-height: 1.25;',
              text: 'Taste More, Pay Less',
            },
            line_3: {
              style:
                'font-family: {font_family}; font-size: 16px; font-weight: 300; color: rgba(0,0,0,.75); margin: 0 0 18px 0;',
              text: 'When you sign up for email and text messages!',
            },
            first_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'hidden',
            },
            last_name: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'hidden',
            },
            birthday: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'required',
            },
            email_address: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'required',
            },
            phone_number: {
              style:
                'font-family: {font_family}; font-size: 14px; width: 100%; background: white; color: rgba(0,0,0,1); border: none; border-bottom: 1px solid rgba(148,148,148,1); border-radius: 0px; padding: 8px 0 0 0;',
              label_style:
                'font-family: {font_family}; font-size: 14px; font-weight: 600; color: {primary_color}; margin-bottom: 6px; display: block;',
              hint_style:
                'text-align: left; font-family: {font_family}; font-size: 12px; color: rgba(0,0,0,0.6); margin-top: 4px;',
              field_mode: 'required',
            },
            agreement: {
              style:
                'font-family: {font_family}; font-size: 12px; text-align: justify; margin: 25px 0 12px 0; color: rgba(0, 0, 0, 0.75);',
            },
            send_button: {
              style:
                'padding: 14px 18px; background-color: {primary_color}; border: 2px solid {primary_color}; border-radius: 8px; width: 100%;',
            },
            send_button_text: {
              style:
                'color: rgba(255,255,255,1); font-family: {font_family}; font-size: 16px; font-weight: bold; text-transform: uppercase;',
            },
            thank_you_line_1: {
              style:
                'font-family: {font_family}; font-size: 20px; font-weight: bold; padding: 45px 0; color: {primary_color};',
            },
            thank_you_line_2: {
              style:
                'font-family: {font_family}; font-size: 18px; padding: 15px 0 55px 0; color: rgba(0,0,0,.75);',
            },
            thank_you_button: {
              style:
                'padding: 10px 0; background-color: {primary_color}; border-radius: 8px; width: 100%;',
            },
            thank_you_button_text: {
              style:
                'font-family: {font_family}; font-size: 16px; color: rgba(255,255,255,1); font-weight: bold; text-transform: uppercase;',
            },
          },
        },
      ],
    };
  },
};
